<template>
  <div class="mt-4">
    <div class="heading-1">Create an Orie account</div>
    <p class="text-3.4 font-400">
      One last step before starting your store setup.
    </p>
  </div>

  <div class="mt-8">
    <v-dynamic-form
      v-model="registration"
      v-model:valid="valid"
      :inputs="inputs"
      :readonly="loading"
      :disabled="inputsDisabled"
      @keyup.enter="submit"
      @submit="submit"
    />

    <v-btn
      @click="submit"
      :loading="loading"
      :disabled="!valid || inputsDisabled"
      color="primary"
      rounded="lg"
      height="38"
      block
      >{{ checkedEmail ? "Create Orie account" : "Continue with email" }}</v-btn
    >

    <div v-if="!checkedEmail" class="my-5 px-8">
      <v-divider>or</v-divider>
    </div>

    <div v-if="!checkedEmail" class="flex flex-col gap-2">
      <v-card
        v-for="value in socialLogins"
        @click="socialSignup(value.provider)"
        class="bg-gray-100"
        :key="value.provider"
        :disabled="inputsDisabled"
        rounded="lg"
        flat
      >
        <div :title="value.name" class="p-2 flex-center flex gap-2">
          <Icon :name="value.icon" size="14" />
          <span class="text-3.5 font-semibold"
            >Signup with {{ value.name }}</span
          >
        </div>
      </v-card>
    </div>

    <div class="mt-8 flex items-center gap-2">
      <span class="caption">Already have a Orie account?</span>
      <v-btn
        class="normal-case"
        :to="{ name: 'login' }"
        :readonly="inputsDisabled"
      >
        Login
      </v-btn>
    </div>

    <div class="mt-8 caption">
      By proceeding, you agree to the
      <a class="link" :href="links.terms" target="_blank"
        >Terms and Conditions</a
      >
      and
      <a class="link" :href="links.privacy" target="_blank">Privacy Policy</a>.
    </div>

    <ConfirmEmail
      @verified="navigateToCreateStore"
      :email="registrationEmail"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import { PasswordInputField } from "#components";
import { Rule } from "~/services/Rules";
import type { UserRegisterInput } from "~/layers/admin/modules/auth/runtime/types/api";
import ConfirmEmail from "@/components/pages/register/ConfirmEmail.vue";

export default defineComponent({
  components: { ConfirmEmail },
  setup() {
    definePageMeta({
      layout: "auth",
      auth: false,
      middleware: "redirect-login",
    });

    const router = useRouter();
    const route = useRoute();
    const { links } = useAppConfig();
    const registrationEmail = ref<string>();
    const registration = ref<UserRegisterInput>();
    const checkedEmail = ref(false);

    const {
      loading,
      inputsDisabled,
      register,
      socialLogins,
      socialLogin,
      checkEmail,
      fetchIntent,
      onEvent,
    } = useSellerAuth();

    const error = ref(false);
    const valid = ref(false);

    const inputs = computed(() => {
      return defineFormInputs({
        email: {
          hideName: true,
          name: "Email",
          type: "text",
          rules: Rule.required(),
          props: {
            label: "Email",
            density: "compact",
            variant: "outlined",
            rounded: "lg",
            readonly: checkedEmail.value,
          },
        },
        name: {
          hideName: true,
          name: "Your Name",
          type: "text",
          rules: Rule.group([Rule.required(), Rule.title()]),
          hidden: !checkedEmail.value,
          props: {
            label: "Your Name",
            density: "compact",
            variant: "outlined",
            rounded: "lg",
          },
        },
        password: {
          hideName: true,
          name: "Password",
          rules: Rule.group([Rule.required(), Rule.password()]),
          component: PasswordInputField,
          class: "mb-2",
          hidden: !checkedEmail.value,
          props: {
            hint: "Use 8 or more characters with a mix of letters, numbers & symbols",
            persistentHint: true,
            label: "Password",
            density: "compact",
            variant: "outlined",
            rounded: "lg",
            type: "password",
          },
        },
        password_confirmation: {
          hideName: true,
          name: "Password confirmation",
          rules: Rule.group([Rule.required(), Rule.confirmed("@password")]),
          component: PasswordInputField,
          hidden: !checkedEmail.value,
          props: {
            label: "Password confirmation",
            density: "compact",
            variant: "outlined",
            rounded: "lg",
            type: "password",
          },
        },
      });
    });

    const submit = () => {
      if (!valid.value || !registration.value) {
        return;
      }
      if (!checkedEmail.value) {
        checkEmail(registration.value!.email).then((exists) => {
          if (exists) {
            router.push({
              name: "login",
              query: { email: registration.value!.email },
            });
          } else {
            checkedEmail.value = true;
          }
        });
      } else {
        return register(registration.value);
      }
    };

    const navigateToCreateStore = () => {
      router.push({ name: "onboarding-new-store" });
    };

    onEvent("register:must-verify-email", (email) => {
      registrationEmail.value = email;
    });

    onEvent("register:error", () => {
      error.value = true;
    });

    onEvent("register:success", navigateToCreateStore);

    onMounted(() => {
      const intent = route.query.intent
        ? String(route.query.intent)
        : undefined;
      if (intent) {
        fetchIntent(intent);
      }
    });

    const socialSignup = (provider: string) => {
      const auth = useAuth();

      // If there's a saved destination, use it; otherwise default to new store flow
      const redirectUrl =
        auth.getDestination() ||
        router.resolve({ name: "onboarding-new-store" }).href;

      return socialLogin(provider, redirectUrl);
    };

    return {
      loading,
      inputsDisabled,
      registration,
      inputs,
      submit,
      error,
      valid,
      socialLogins,
      socialSignup,
      checkedEmail,
      navigateToCreateStore,
      registrationEmail,
      links,
    };
  },
});
</script>
